import React, { useEffect, useState } from 'react';
import { Coins, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { fmtWhen, rulesLine } from '../../lib/doctorView';
import { DOCTOR_TAG } from '../../lib/doctorSwitch';
import { tone, LevelIcon, DoctorFix, HoldWhen } from './DoctorParts';

/*
 * r16sd (design 5.3, Layout): the account banner - account-wide holds from /uiapi/me doctor.accountHolds (0 post credits on N
 * slots, 0 target credits, the account paused, finding posts down on our side, a PC missing FFmpeg ...) at the top of every page,
 * red first, two at a time with "Show N more". The words and the fix ("Contact support to top up" is a hint, never a button) are the
 * server's. Nothing is drawn when the answer carries none.
 */
export function AccountBanner({ holds }) {
  const [all, setAll] = useState(false);
  const list = Array.isArray(holds) ? holds : [];
  const sig = rulesLine(list);
  useEffect(() => {
    if (!list.length) return;
    // eslint-disable-next-line no-console
    console.info(`${DOCTOR_TAG} account banners: ${sig}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 2);
  return (
    <section className="mb-4 space-y-2 sm:mb-5" aria-label="Account notices" data-testid="account-banner">
      {shown.map((h, i) => {
        const t = tone(h.level);
        return (
          <div key={`${h.rule}-${i}`} className={cn('flex flex-col gap-1.5 rounded-2xl border px-4 py-2.5 sm:flex-row sm:items-center sm:gap-4 sm:py-3', t.box)}
            data-testid={`account-banner-${h.rule}`} data-level={h.level} role="status">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <LevelIcon level={h.level} className="mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium leading-relaxed text-foreground text-pretty">{h.text}</p>
                <HoldWhen hold={h} fmt={fmtWhen} className="block" />
              </div>
            </div>
            {h.fix && (
              <div className="flex shrink-0 pl-7 sm:pl-0">
                <DoctorFix fix={h.fix} settings={h.settings} testid={`account-banner-fix-${h.rule}`} rule={h.rule} nearText={h.text} />
              </div>
            )}
          </div>
        );
      })}
      {list.length > 2 && (
        <button type="button" onClick={() => setAll((v) => !v)} aria-expanded={all}
          className="tap inline-flex items-center gap-1 px-1 text-xs text-muted-foreground hover:text-foreground" data-testid="account-banner-more">
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', all && 'rotate-180')} aria-hidden />
          {all ? 'Show fewer' : `Show ${list.length - 2} more`}
        </button>
      )}
    </section>
  );
}

const fmtN = (v) => (v === null || v === undefined ? '-' : Number(v).toLocaleString());

/*
 * The post and target credit balances (design 4.1 item 3: V3 showed them nowhere). Read-only. Two labelled chips from sm up; on a
 * phone one compact chip that opens the two figures. A balance at 0 is in the danger tone only while the account uses that credit
 * (`inUse`, lib/doctorView.js creditsInUse; review SF7) - otherwise the neutral tone. Not drawn while credits are not metered.
 */
export function CreditsChip({ credits: raw, inUse }) {
  if (!raw) return null;
  const credits = {
    ...raw,
    postsOut: !!raw.postsOut && (!inUse || !!inUse.posts),
    targetsOut: !!raw.targetsOut && (!inUse || !!inUse.targets),
  };
  const chip = (out) => cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tnum ring-1',
    out ? 'bg-danger/12 text-danger ring-danger/30' : 'bg-secondary/70 text-foreground/85 ring-border');
  const title = `Post credits left: ${fmtN(credits.posts)} · Target credits left: ${fmtN(credits.targets)}`;
  return (
    <div className="flex items-center" data-testid="credits-chip">
      <div className="hidden items-center gap-1.5 sm:flex" title={title}>
        {credits.posts !== null && (
          <span className={chip(credits.postsOut)} data-testid="credits-posts" data-out={credits.postsOut ? 'true' : undefined}>
            <Coins className="h-3.5 w-3.5" aria-hidden />
            <span className="text-muted-foreground">Post credits</span> {fmtN(credits.posts)}
          </span>
        )}
        {credits.targets !== null && (
          <span className={chip(credits.targetsOut)} data-testid="credits-targets" data-out={credits.targetsOut ? 'true' : undefined}>
            <span className="text-muted-foreground">Target credits</span> {fmtN(credits.targets)}
          </span>
        )}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={cn(chip(credits.postsOut || credits.targetsOut), 'tap sm:hidden')} aria-label={title}
            data-testid="credits-phone">
            <Coins className="h-3.5 w-3.5" aria-hidden />
            {fmtN(credits.posts)}<span className="text-muted-foreground/70">/</span>{fmtN(credits.targets)}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-60 space-y-2 text-sm" data-testid="credits-popover">
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Post credits</span>
            <span className={cn('font-semibold tnum', credits.postsOut && 'text-danger')}>{fmtN(credits.posts)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Target credits</span>
            <span className={cn('font-semibold tnum', credits.targetsOut && 'text-danger')}>{fmtN(credits.targets)}</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
